// src/features/project/AnalyticsPanel.tsx
// Analytics: GetProjectStats headline + GetVelocity + GetCumulativeFlow charts, and
// the burndown of the active sprint (GetSprintBurndown).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectAnalytic, useProjectSprint } from '@react/hooks';
import { Card, CardContent, ErrorAlert } from '@react/components';
import { BurndownChart, VelocityChart, CumulativeFlowChart } from './charts';
import { idOf, listParams } from './meta';
import type { Entity } from '@core/types/entity';
import type { ProjectStats, VelocityData, CumulativeFlowData, BurndownData } from '@core/types/project';

export const AnalyticsPanel = observer(({ projectEntity }: { projectEntity: Entity }) => {
  const { t } = useI18n();
  const analytics = useProjectAnalytic();
  const sprintStore = useProjectSprint();
  const projectId = projectEntity.id;

  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [velocity, setVelocity] = useState<VelocityData | null>(null);
  const [flow, setFlow] = useState<CumulativeFlowData | null>(null);
  const [burndown, setBurndown] = useState<BurndownData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      analytics.GetProjectStats(projectId).then((r) => alive && setStats(r.stats ?? null)),
      analytics.GetVelocity(projectId).then((r) => alive && setVelocity(r.velocity ?? null)),
      analytics.GetCumulativeFlow(projectId).then((r) => alive && setFlow(r.cumulative_flow ?? null)),
      sprintStore.ListSprints(projectId, listParams()).then(async () => {
        const active = sprintStore.items.find((s) => s.status === 'SPRINT_STATUS_ACTIVE');
        if (active) {
          const r = await sprintStore.GetSprintBurndown(projectId, idOf(active));
          if (alive) setBurndown(r.burndown ?? null);
        }
      }),
    ]).finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId]);

  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-lg bg-muted/50" />
        ))}
      </div>
    );
  }

  if (analytics.error) {
    return <ErrorAlert error={analytics.error} dismissible onDismiss={() => analytics.clearError()} />;
  }

  const chartLabels = {
    burndown: {
      title: t('project.analytics.burndown'),
      ideal: t('project.analytics.ideal'),
      remaining: t('project.analytics.remaining'),
      empty: t('project.analytics.noData'),
    },
    velocity: {
      title: t('project.analytics.velocity'),
      committed: t('project.analytics.committed'),
      completed: t('project.analytics.completed'),
      average: t('project.analytics.average'),
      empty: t('project.analytics.noData'),
    },
    flow: { title: t('project.analytics.cumulativeFlow'), empty: t('project.analytics.noData') },
  };

  return (
    <div className="space-y-6">
      {/* Headline stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile label={t('project.analytics.totalItems')} value={stats.total_items ?? 0} />
          <StatTile label={t('project.analytics.completedItems')} value={stats.completed_items ?? 0} />
          <StatTile label={t('project.analytics.inProgress')} value={stats.in_progress_items ?? 0} />
          <StatTile label={t('project.analytics.blocked')} value={stats.blocked_items ?? 0} tone={stats.blocked_items ? 'warn' : undefined} />
          <StatTile label={t('project.analytics.overdue')} value={stats.overdue_items ?? 0} tone={stats.overdue_items ? 'bad' : undefined} />
          <StatTile label={t('project.analytics.points')} value={`${stats.completed_points ?? 0}/${stats.total_points ?? 0}`} />
          <StatTile label={t('project.analytics.avgVelocity')} value={Math.round(stats.avg_velocity ?? 0)} />
          <StatTile
            label={t('project.analytics.cycleTime')}
            value={stats.avg_cycle_time_hours ? `${Math.round(stats.avg_cycle_time_hours)}h` : '—'}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">{t('project.analytics.velocity')}</h3>
            <VelocityChart
              sprints={velocity?.sprints ?? []}
              averageVelocity={velocity?.average_velocity ?? 0}
              labels={chartLabels.velocity}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">{t('project.analytics.activeBurndown')}</h3>
            <BurndownChart
              points={burndown?.points ?? []}
              totalPoints={burndown?.total_points ?? 0}
              labels={chartLabels.burndown}
            />
          </CardContent>
        </Card>
        <Card className="xl:col-span-2">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">{t('project.analytics.cumulativeFlow')}</h3>
            <CumulativeFlowChart
              points={flow?.points ?? []}
              columns={flow?.columns ?? []}
              labels={chartLabels.flow}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
});

AnalyticsPanel.displayName = 'AnalyticsPanel';

function StatTile({ label, value, tone }: { label: string; value: string | number; tone?: 'warn' | 'bad' }) {
  const valueClass =
    tone === 'bad' ? 'text-destructive' : tone === 'warn' ? 'text-accent-foreground' : 'text-foreground';
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</p>
    </div>
  );
}
